import { useAppContext } from 'AppContext'
import type { Bundle, BundleData } from 'types/bundle'
import getEnv from 'utils/getEnv'

/**
 * A wrapper around `fetch` that re-tries if it fails
 *
 *
 * @param url URL to fetch
 * @param retries number of retries to refresh the token
 * @category HTTP
 */
export async function repeatableFetch(
  url: string,
  retries: number = 1,
): ReturnType<typeof fetch> {
  // Create header with api token to the request
  console.log('token', import.meta.env.VITE_APP_API_URL)

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'content-type': 'application/json',
    },
  })

  if (response.status === 401 && retries > 0) {
    // Try again
    return repeatableFetch(url, retries - 1)
  }

  return response
}

// Parse Bundle data
export const parseBundle = (data: BundleData): Bundle => {
  return {
    songs: data,
  }
}

export function useBundleData() {
  const { bundleLoader, contentLoaded } = useAppContext()

  const getSongs = () => {
    if (!bundleLoader || !contentLoaded) return []

    return bundleLoader.songs
  }

  return {
    getSongs,
  }
}

export function useBundle() {
  const env = getEnv()

  const fetchAndParse = async <TResponse>(url: string): Promise<TResponse> => {
    const response = await repeatableFetch(url)

    if (!response.ok) {
      const json = await response.json()
      throw json || { code: response.status, detail: response.statusText }
    }

    const json = await response.json()

    return json
  }

  const BUNDLE = () => `${env.VITE_APP_API_URL}songs`

  const fullBundle = async () => {
    if (!env.VITE_APP_API_URL)
      throw new Error('VITE_APP_API_URL missing from .env')

    const data = await Promise.all([fetchAndParse<BundleData>(BUNDLE())]).catch(
      (_) => {
        throw new Error('Cannot load data')
      },
    )

    const [bundleData] = data
    const parsedBundleData = parseBundle(bundleData)

    const bundle: Bundle = {
      ...parsedBundleData,
    }
    console.info('Bundle', bundle)
    if (bundle) {
      return bundle
    } else {
      throw new Error('Bundle is empty')
    }
  }

  return {
    fullBundle,
  }
}
